const ARA_CART_UPDATE_EVENT = 'cart:update';

const cartRoot = () => window.Shopify?.routes?.root || '/';

const cartSectionIds = () => {
  const ids = new Set();
  document.querySelectorAll('cart-items-component[data-section-id]').forEach((component) => {
    const id = component.dataset.sectionId;
    if (id) ids.add(id);
  });
  return [...ids];
};

const dispatchCartUpdate = (sections = {}) => {
  document.dispatchEvent(
    new CustomEvent(ARA_CART_UPDATE_EVENT, {
      bubbles: true,
      detail: {
        data: {
          source: 'ara-offers',
          sections,
        },
      },
    })
  );
};

const requestCart = async (path, body) => {
  const response = await fetch(`${cartRoot()}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(body),
  });

  const data = await response.json();
  if (!response.ok || data.status) {
    throw new Error(data.description || data.message || 'Unable to update the cart.');
  }
  return data;
};

const addItems = async (items) => {
  const validItems = items.filter((item) => Number(item.id) > 0);
  if (!validItems.length) return null;

  const sections = cartSectionIds();
  return requestCart('cart/add.js', {
    items: validItems,
    sections: sections.join(','),
    sections_url: window.location.pathname,
  });
};

const removeLineKeys = async (keys, includeSections = false) => {
  const validKeys = keys.filter(Boolean);
  let response = null;

  for (let index = 0; index < validKeys.length; index += 1) {
    const body = {
      id: validKeys[index],
      quantity: 0,
    };

    if (includeSections && index === validKeys.length - 1) {
      const sections = cartSectionIds();
      body.sections = sections.join(',');
      body.sections_url = window.location.pathname;
    }

    response = await requestCart('cart/change.js', body);
  }

  return response;
};

class AraOfferCard extends HTMLElement {
  #abortController = new AbortController();

  connectedCallback() {
    this.addEventListener('click', this.#onClick, { signal: this.#abortController.signal });
  }

  disconnectedCallback() {
    this.#abortController.abort();
  }

  #onClick = async (event) => {
    const button = event.target.closest('[data-ara-action]');
    if (!(button instanceof HTMLButtonElement)) return;

    const action = button.dataset.araAction;
    if (!action) return;

    event.preventDefault();
    await this.#perform(action);
  };

  async #perform(action) {
    const status = this.querySelector('[data-ara-status]');
    const buttons = [...this.querySelectorAll('button[data-ara-action]')];
    const bundleVariantId = Number(this.dataset.bundleVariantId || 0);
    const partnerVariantId = Number(this.dataset.partnerVariantId || 0);
    const workbookVariantId = Number(this.dataset.workbookVariantId || 0);
    const bundleProductId = this.dataset.bundleProductId || '';
    const removeKeys = (this.dataset.removeKeys || '').split(',').map((key) => key.trim()).filter(Boolean);

    const setBusy = (busy) => {
      buttons.forEach((item) => {
        item.disabled = busy || item.dataset.wasDisabled === 'true';
      });
    };

    buttons.forEach((item) => {
      if (item.disabled) item.dataset.wasDisabled = 'true';
    });

    setBusy(true);
    if (status) status.textContent = 'Updating your cart…';

    try {
      let response = null;

      if (action === 'bundle') {
        if (!bundleVariantId) throw new Error('Bundle product is not configured yet.');

        const items = [
          {
            id: bundleVariantId,
            quantity: 1,
          },
        ];

        if (workbookVariantId) {
          items.push({
            id: workbookVariantId,
            quantity: 1,
            properties: {
              _ara_free_workbook: 'true',
              _ara_bundle_product_id: String(bundleProductId),
            },
          });
        }

        response = await addItems(items);

        if (removeKeys.length) {
          response = await removeLineKeys(removeKeys, true);
        }
      }

      if (action === 'partner') {
        if (!partnerVariantId) throw new Error('Matching product is not configured yet.');
        response = await addItems([{ id: partnerVariantId, quantity: 1 }]);
      }

      if (action === 'workbook') {
        if (!workbookVariantId) throw new Error('Workbook product is not configured yet.');
        response = await addItems([{ id: workbookVariantId, quantity: 1 }]);
      }

      if (status) status.textContent = 'Added to your cart.';
      dispatchCartUpdate(response?.sections || {});
    } catch (error) {
      if (status) status.textContent = error instanceof Error ? error.message : 'Unable to update the cart.';
      setBusy(false);
    }
  }
}

class AraAutoWorkbook extends HTMLElement {
  connectedCallback() {
    if (this.dataset.running === 'true') return;
    this.dataset.running = 'true';
    this.#run();
  }

  async #run() {
    const workbookVariantId = Number(this.dataset.workbookVariantId || 0);
    const bundleProductId = this.dataset.bundleProductId || '';
    if (!workbookVariantId || !bundleProductId) return;

    try {
      const response = await addItems([
        {
          id: workbookVariantId,
          quantity: 1,
          properties: {
            _ara_free_workbook: 'true',
            _ara_bundle_product_id: String(bundleProductId),
          },
        },
      ]);
      dispatchCartUpdate(response?.sections || {});
    } catch (error) {
      console.error(error);
    }
  }
}

class AraOrphanWorkbook extends HTMLElement {
  connectedCallback() {
    if (this.dataset.running === 'true') return;
    this.dataset.running = 'true';
    this.#run();
  }

  async #run() {
    const key = this.dataset.lineKey;
    if (!key) return;

    try {
      const response = await removeLineKeys([key], true);
      dispatchCartUpdate(response?.sections || {});
    } catch (error) {
      console.error(error);
    }
  }
}

if (!customElements.get('ara-offer-card')) {
  customElements.define('ara-offer-card', AraOfferCard);
}

if (!customElements.get('ara-auto-workbook')) {
  customElements.define('ara-auto-workbook', AraAutoWorkbook);
}

if (!customElements.get('ara-orphan-workbook')) {
  customElements.define('ara-orphan-workbook', AraOrphanWorkbook);
}
